const Theme = (window.Theme = window.Theme || {});

const DEBOUNCE_MS = 200;
const CACHE_LIMIT = 30;
const RECENT_KEY = 'convert:recent-searches';
const RECENT_LIMIT = 5;

function debounce(fn, wait) {
  let timer;
  const debounced = (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
  debounced.cancel = () => clearTimeout(timer);
  return debounced;
}

function readRecent() {
  try {
    const value = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

function writeRecent(list) {
  try {
    if (list.length) localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    else localStorage.removeItem(RECENT_KEY);
  } catch {
    /* storage unavailable */
  }
}

class SearchOverlay {
  constructor(dialog) {
    this.dialog = dialog;
    this.form = dialog.querySelector('[data-search-form]');
    this.input = dialog.querySelector('[data-search-input]');
    this.clearButton = dialog.querySelector('[data-search-clear]');
    this.closeButton = dialog.querySelector('[data-search-close]');
    this.body = dialog.querySelector('[data-search-body]');
    this.intro = dialog.querySelector('[data-search-intro]');
    this.results = dialog.querySelector('[data-search-results]');
    this.status = dialog.querySelector('[data-search-status]');
    this.trending = dialog.querySelector('[data-search-trending]');
    this.recent = dialog.querySelector('[data-search-recent]');
    this.recentList = dialog.querySelector('[data-search-recent-list]');
    this.recentClear = dialog.querySelector('[data-search-recent-clear]');
    this.skeleton = dialog.querySelector('[data-search-skeleton]');
    this.sectionId = dialog.dataset.sectionId || 'predictive-search';

    this.cache = new Map();
    this.controller = null;
    this.options = [];
    this.activeIndex = -1;
    this.currentTerm = '';
    this.opener = null;
    this.queueSearch = debounce(() => this.search(this.term), DEBOUNCE_MS);

    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.form.addEventListener('reset', () => this.onReset());
    this.closeButton?.addEventListener('click', () => this.close());
    this.recentClear?.addEventListener('click', () => {
      writeRecent([]);
      this.renderRecent();
      this.input.focus();
    });
    this.dialog.addEventListener('click', (event) => this.onClick(event));
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.close();
    });
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    this.dialog.addEventListener('close', () => this.onClose());
    this.results.addEventListener('mousemove', (event) => {
      const option = event.target.closest('[role="option"]');
      if (option && this.options.includes(option)) this.select(this.options.indexOf(option), { scroll: false });
    });
  }

  get term() {
    return this.input.value.trim();
  }

  get endpoint() {
    const root = Theme.routes?.root || window.Shopify?.routes?.root || '/';
    return Theme.routes?.predictiveSearch || `${root.replace(/\/$/, '')}/search/suggest`;
  }

  open(opener) {
    if (this.dialog.open) {
      this.input.focus();
      return;
    }
    this.opener = opener instanceof HTMLElement ? opener : document.activeElement;
    this.renderRecent();
    this.dialog.showModal();
    Theme.lockScroll?.(true);
    this.opener?.setAttribute?.('aria-expanded', 'true');
    requestAnimationFrame(() => {
      this.input.focus({ preventScroll: true });
      this.input.select();
    });
    if (this.term) this.search(this.term);
    document.dispatchEvent(new CustomEvent('search:opened'));
  }

  close() {
    if (!this.dialog.open) return;
    this.queueSearch.cancel();
    this.controller?.abort();
    this.dialog.close();
  }

  onClose() {
    Theme.lockScroll?.(false);
    this.setLoading(false);
    this.select(-1);
    this.opener?.setAttribute?.('aria-expanded', 'false');
    if (this.opener && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
    this.opener = null;
    document.dispatchEvent(new CustomEvent('search:closed'));
  }

  onClick(event) {
    if (event.target === this.dialog) {
      this.close();
      return;
    }
    const link = event.target.closest('a[href]');
    if (link && this.results.contains(link) && this.term) this.remember(this.term);
  }

  onInput() {
    const term = this.term;
    this.clearButton.hidden = this.input.value.length === 0;
    if (!term) {
      this.queueSearch.cancel();
      this.controller?.abort();
      this.showIntro();
      return;
    }
    if (this.cache.has(term)) {
      this.queueSearch.cancel();
      this.controller?.abort();
      this.render(term, this.cache.get(term));
      return;
    }
    this.showLoading();
    this.queueSearch();
  }

  onReset() {
    requestAnimationFrame(() => {
      this.clearButton.hidden = true;
      this.queueSearch.cancel();
      this.controller?.abort();
      this.showIntro();
      this.input.focus();
    });
  }

  onSubmit(event) {
    const term = this.term;
    if (!term) {
      event.preventDefault();
      this.input.focus();
      return;
    }
    this.remember(term);
  }

  onKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        if (!this.options.length) return;
        event.preventDefault();
        this.select(this.activeIndex + 1 >= this.options.length ? 0 : this.activeIndex + 1);
        break;
      case 'ArrowUp':
        if (!this.options.length) return;
        event.preventDefault();
        this.select(this.activeIndex <= 0 ? this.options.length - 1 : this.activeIndex - 1);
        break;
      case 'Enter': {
        const option = this.options[this.activeIndex];
        const link = option?.querySelector('a[href]');
        if (!link) return;
        event.preventDefault();
        if (this.term) this.remember(this.term);
        link.click();
        break;
      }
      default:
    }
  }

  select(index, { scroll = true } = {}) {
    this.options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
    this.activeIndex = index;
    const option = this.options[index];
    if (option) {
      this.input.setAttribute('aria-activedescendant', option.id);
      if (scroll) option.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  buildUrl(term) {
    const url = new URL(this.endpoint, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product,collection,page,article,query');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][prefix]', 'last');
    url.searchParams.set('section_id', this.sectionId);
    return url;
  }

  async search(term) {
    if (!term) return;
    if (this.cache.has(term)) {
      this.render(term, this.cache.get(term));
      return;
    }
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.setLoading(true);
    try {
      const response = await fetch(this.buildUrl(term), { signal: controller.signal });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
      const html = this.extract(await response.text());
      this.store(term, html);
      if (this.term === term) this.render(term, html);
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (this.term === term) this.renderError();
    } finally {
      if (this.controller === controller) {
        this.controller = null;
        this.setLoading(false);
      }
    }
  }

  extract(text) {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return doc.querySelector('[data-predictive-results]')?.outerHTML || '';
  }

  store(term, html) {
    if (this.cache.has(term)) this.cache.delete(term);
    this.cache.set(term, html);
    if (this.cache.size > CACHE_LIMIT) this.cache.delete(this.cache.keys().next().value);
  }

  setLoading(loading) {
    this.dialog.toggleAttribute('data-loading', loading);
    this.results.setAttribute('aria-busy', String(loading));
  }

  showIntro() {
    this.setLoading(false);
    this.currentTerm = '';
    this.options = [];
    this.select(-1);
    this.input.setAttribute('aria-expanded', 'false');
    this.results.hidden = true;
    this.results.replaceChildren();
    this.intro.hidden = false;
    this.renderRecent();
    this.announce('');
  }

  showLoading() {
    this.intro.hidden = true;
    this.results.hidden = false;
    this.options = [];
    this.select(-1);
    if (!this.results.querySelector('[data-predictive-results]') && this.skeleton) {
      this.results.replaceChildren(this.skeleton.content.cloneNode(true));
    }
    this.setLoading(true);
    this.announce(this.dialog.dataset.statusLoading || '');
  }

  render(term, html) {
    this.currentTerm = term;
    this.intro.hidden = true;
    this.results.hidden = false;
    this.results.innerHTML = html;
    this.setLoading(false);

    const root = this.results.querySelector('[data-predictive-results]');
    const empty = this.results.querySelector('[data-predictive-empty]');
    const suggestions = empty?.querySelector('[data-predictive-suggestions]');
    if (suggestions && this.trending) suggestions.replaceChildren(this.trending.cloneNode(true));

    this.options = [...this.results.querySelectorAll('[role="option"]')];
    this.select(-1);
    this.input.setAttribute('aria-expanded', String(this.options.length > 0));
    this.body.scrollTop = 0;

    const count = Number(root?.dataset.count || 0);
    const template = count > 0 ? this.dialog.dataset.statusResults : this.dialog.dataset.statusEmpty;
    this.announce((template || '').replace('__N__', String(count)));
  }

  renderError() {
    this.options = [];
    this.select(-1);
    this.input.setAttribute('aria-expanded', 'false');
    const message = document.createElement('p');
    message.className = 'predictive-search__empty text-muted';
    message.textContent = this.dialog.dataset.statusError || '';
    this.results.replaceChildren(message);
    this.announce(message.textContent);
  }

  announce(message) {
    if (!this.status) return;
    clearTimeout(this.announceTimer);
    this.announceTimer = setTimeout(() => {
      this.status.textContent = message;
    }, 350);
  }

  remember(term) {
    const value = term.slice(0, 80);
    const list = readRecent().filter((item) => item.toLowerCase() !== value.toLowerCase());
    list.unshift(value);
    writeRecent(list.slice(0, RECENT_LIMIT));
  }

  renderRecent() {
    if (!this.recent || !this.recentList) return;
    const list = readRecent();
    this.recent.hidden = list.length === 0;
    const action = this.form.getAttribute('action') || '/search';
    this.recentList.replaceChildren(
      ...list.map((term) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        const url = new URL(action, window.location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'last');
        link.href = `${url.pathname}${url.search}`;
        link.className = 'pill search-overlay__chip';
        link.textContent = term;
        item.append(link);
        return item;
      })
    );
  }
}

function init() {
  const dialog = document.querySelector('[data-search-overlay]');
  if (!dialog || dialog.searchOverlay) return;
  const overlay = new SearchOverlay(dialog);
  dialog.searchOverlay = overlay;

  Theme.search = {
    open: (opener) => overlay.open(opener),
    close: () => overlay.close(),
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-search-open]');
    if (!opener || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    overlay.open(opener);
  });

  document.addEventListener('search:open', (event) => overlay.open(event.detail?.opener));

  document.addEventListener('keydown', (event) => {
    if (dialog.open || event.defaultPrevented) return;
    const target = event.target;
    const typing = target instanceof HTMLElement && (target.isContentEditable || target.closest('input, textarea, select'));
    const shortcut = (event.key === 'k' || event.key === 'K') && (event.metaKey || event.ctrlKey);
    if (shortcut || (event.key === '/' && !typing)) {
      event.preventDefault();
      overlay.open(document.activeElement);
    }
  });

  document.querySelectorAll('[data-search-open]').forEach((element) => {
    element.setAttribute('aria-haspopup', 'dialog');
    element.setAttribute('aria-controls', dialog.id);
    element.setAttribute('aria-expanded', 'false');
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

export default SearchOverlay;
